import React, { useState, useMemo } from 'react';
import { NavArrowUp, NavArrowDown, NavArrowLeft, EditPencil, Check, WarningTriangle } from 'iconoir-react';
import { C, St, CARD_STYLE, FONT_BODY, focus, blur } from '../theme';
import { SearchBar } from '../components/UI';
import { MOOD_RATINGS } from '../components/Forms';
import { calcVolume, detectStalls, calcMuscleVolume, assessWeeklyVolume } from '../science';
import { findExercise } from '../exerciseDB';
import { saveDB } from '../database';

import { SchedaSelect } from '../components/Session';
import { ExercisePreview } from '../components/Exercise';
import { buildColorMap, sessionFitsScheda } from '../utils';
import { FolderIcon } from '../components/Folder';


// ─── SCHEDE WIDGET ───────────────────────────────────────────────────────────
function SchedeWidget({ schede, sessions, activeScheda, onSelect, colorMap }) {
  if (!schede || schede.length === 0) return null;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, 88px)", gap: 16, justifyContent: "start", paddingBottom: 20, marginBottom: 4 }}>
      {schede.map(s => {
        const col = colorMap[s.name] || C.blue;
        const cnt = sessions.filter(x => sessionFitsScheda(x.workoutName, s.name)).length;
        return (
          <FolderIcon
            key={s.id}
            accentColor={col}
            label={s.name}
            sublabel={cnt > 0 ? `${cnt} sess.` : null}
            isActive={activeScheda === s.name}
            onClick={() => onSelect(activeScheda === s.name ? "all" : s.name)}
            size={88}
          />
        );
      })}
    </div>
  );
}

export function HistorySection({ db, setDB }) {
  const [openId, setOpenId] = useState(null);
  const [activeScheda, setActiveScheda] = useState("all");
  const [search, setSearch] = useState("");
  const [editDateId, setEditDateId] = useState(null);
  const [editDateVal, setEditDateVal] = useState("");
  const [editMode, setEditMode] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const colorMap = useMemo(() => buildColorMap(db.schede || [], db.sessions), [db.schede, db.sessions]);

  const del = id => { const u = { ...db, sessions: db.sessions.filter(s => s.id !== id) }; setDB(u); saveDB(u); };
  const saveDate = id => { if (!editDateVal) return; const u = { ...db, sessions: db.sessions.map(s => s.id === id ? { ...s, date: editDateVal } : s) }; setDB(u); saveDB(u); setEditDateId(null); };
  const saveScheda = (id, name) => { const u = { ...db, sessions: db.sessions.map(s => s.id === id ? { ...s, workoutName: name } : s) }; setDB(u); saveDB(u); };
  const removeExercise = (sessId, exIdx) => { const u = { ...db, sessions: db.sessions.map(s => s.id === sessId ? { ...s, exercises: s.exercises.filter((_, i) => i !== exIdx) } : s) }; setDB(u); saveDB(u); };
  const updateExercise = (sessId, exIdx, updatedEx) => { const u = { ...db, sessions: db.sessions.map(s => s.id === sessId ? { ...s, exercises: s.exercises.map((e, i) => i === exIdx ? updatedEx : e) } : s) }; setDB(u); saveDB(u); };

  const currentMonth = new Date().toISOString().slice(0, 7);

  const sorted = useMemo(() => {
    let s = [...db.sessions];
    if (activeScheda !== "all") s = s.filter(x => sessionFitsScheda(x.workoutName, activeScheda));
    if (search.trim()) { const q = search.toLowerCase(); s = s.filter(x => x.workoutName?.toLowerCase().includes(q) || x.exercises.some(e => e.name.toLowerCase().includes(q))); }
    s.sort((a, b) => b.date.localeCompare(a.date));
    return s;
  }, [db.sessions, activeScheda, search]);

  const isFiltered = !!search.trim();
  const visible = useMemo(() =>
    (showAll || isFiltered) ? sorted : sorted.filter(x => x.date.slice(0, 7) === currentMonth),
  [sorted, showAll, isFiltered, currentMonth]);
  const hiddenCount = sorted.length - visible.length;

  const grouped = useMemo(() => {
    const map = new Map();
    visible.forEach(sess => {
      const d = new Date(sess.date);
      const day = (d.getDay() + 6) % 7;
      const mon = new Date(d); mon.setDate(d.getDate() - day);
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      const fmt = dt => dt.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
      const key = `${mon.toISOString().slice(0,10)}__${fmt(mon)} - ${fmt(sun)}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(sess);
    });
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [visible]);

  // ─── DETAIL PAGE ─────────────────────────────────────────────────────────────
  if (openId) {
    const sess = db.sessions.find(s => s.id === openId);
    if (sess) {
      const prevSessions = db.sessions.filter(s => s.date < sess.date);
      const totalVol = Math.round(sess.exercises.reduce((s, e) => s + calcVolume(e.sets), 0));
      const totalSets = sess.exercises.reduce((s, e) => s + (e.sets || []).filter(x => parseFloat(x.weight) > 0 && parseInt(x.reps) > 0).length, 0);
      const totalReps = sess.exercises.reduce((s, e) => s + (e.sets || []).reduce((a, x) => a + (parseInt(x.reps) || 0), 0), 0);
const prevSessForDelta = prevSessions.find(s => sess.exercises.some(e => s.exercises?.some(pe => pe.name.toLowerCase() === e.name.toLowerCase())));
      const prevTotalVol = prevSessForDelta ? Math.round(prevSessForDelta.exercises.reduce((s, e) => s + calcVolume(e.sets || []), 0)) : null;
      const volDelta = prevTotalVol && prevTotalVol > 0 ? ((totalVol - prevTotalVol) / prevTotalVol * 100) : null;

      return (
        <>
        <div style={{ position: 'fixed', inset: 0, zIndex: 450, background: C.bg, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
          {/* Header */}
          <div style={{ position: 'sticky', top: 0, zIndex: 10, background: C.bg, paddingTop: 'max(52px, calc(env(safe-area-inset-top, 0px) + 12px))', paddingBottom: 6, borderBottom: '0.5px solid rgba(60,60,67,0.10)' }}>
            <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 16, paddingRight: 20, maxWidth: 560, margin: '0 auto', position: 'relative' }}>
              <button onClick={() => { setOpenId(null); setEditMode(null); setEditDateId(null); }} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: C.blue, padding: '4px 0', fontFamily: FONT_BODY, fontSize: 17, fontWeight: 400 }}>
                <NavArrowLeft width={16} height={16}/>
                Indietro
              </button>
              <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', fontWeight: 600, fontSize: 17, color: C.text, fontFamily: FONT_BODY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '55%' }}>
                {sess.workoutName || 'Allenamento'}
              </div>
            </div>
          </div>

          {/* Content */}
          <div style={{ padding: '20px 20px 80px', maxWidth: 560, margin: '0 auto', width: '100%' }}>
            {/* Volume hero + Azioni — stessa riga */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
              <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4, marginBottom: 4, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 38, fontWeight: 800, color: C.text, letterSpacing: -1.5, lineHeight: 1, fontFamily: FONT_BODY }}>{totalVol.toLocaleString('it-IT')}</span>
                <span style={{ fontSize: 15, color: C.subtle, fontFamily: FONT_BODY }}>kg·reps</span>
                {volDelta !== null && (
                  <span style={{ ...St.tag(volDelta === 0 ? C.subtle : volDelta > 0 ? C.green : C.red), fontSize: 13 }}>
                    {volDelta > 0 ? '+' : ''}{volDelta.toFixed(1)}%
                  </span>
                )}
              </div>
              <div style={{ fontSize: 13, color: C.subtle, fontFamily: FONT_BODY }}>{totalSets} serie · {totalReps} reps · {sess.exercises.length} esercizi</div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginTop: 4 }}>
                <button
                  onClick={() => { setEditMode(editMode === sess.id ? null : sess.id); setEditDateId(null); }}
                  style={{ background: editMode === sess.id ? `${C.blue}12` : "rgba(120,120,128,0.10)", border: "none", color: C.blue, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: FONT_BODY, padding: "6px 14px", borderRadius: 99, display: "flex", alignItems: "center", gap: 4 }}>
                  {editMode === sess.id
                    ? <><Check width={12} height={12} strokeWidth={2.5}/> Fine</>
                    : <><EditPencil width={12} height={12} strokeWidth={2}/> Modifica</>}
                </button>
                {editMode !== sess.id && (
                  <button
                    onClick={() => setConfirmDeleteId(sess.id)}
                    style={{ background: `${C.red}12`, border: "none", color: C.red, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: FONT_BODY, padding: "6px 14px", borderRadius: 99, display: "flex", alignItems: "center", gap: 4 }}>
                    Elimina
                  </button>
                )}
              </div>
            </div>

            {editMode === sess.id && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                <div><label style={St.label}>Data</label>
                  {editDateId === sess.id
                    ? <div style={{ display: "flex", gap: 6 }}>
                      <input type="date" value={editDateVal} onChange={e => setEditDateVal(e.target.value)} style={{ ...St.input, flex: 1, fontSize: 13 }} onFocus={focus} onBlur={blur} autoFocus />
                      <button onClick={() => saveDate(sess.id)} style={{ ...St.btn(C.green), padding: "8px 12px", fontSize: 13, borderRadius: 12 }}>
                        <Check width={14} height={14} strokeWidth={2.5}/>
                      </button>
                    </div>
                    : <button onClick={() => { setEditDateId(sess.id); setEditDateVal(sess.date); }}
                      style={{ ...St.input, cursor: "pointer", fontSize: 13, color: C.text, background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span>{new Date(sess.date).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })}</span>
                      <EditPencil width={13} height={13} color={C.subtle} strokeWidth={2}/>
                    </button>}
                </div>
                <div><label style={St.label}>Scheda</label>
                  <SchedaSelect schede={db.schede || []} sessions={db.sessions} value={sess.workoutName || ""} onChange={n => saveScheda(sess.id, n)} />
                </div>
              </div>
            )}

            {/* Wellness card */}
            {sess.wellness && (() => {
              const w = sess.wellness;
              const m = w.mood > 0 ? MOOD_RATINGS[w.mood - 1] : null;
              const col = w.score >= 7 ? C.green : w.score >= 5 ? C.blue : C.amber;
              return (
                <div style={{ ...St.card, background: m?.bg || C.bg, marginBottom: 14, padding: "14px 18px" }}>
                  <div style={St.chip(col)}>Come stavi</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                    {m && <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 24 }}>{m.emoji}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: m.color }}>{m.label}</span>
                    </div>}
                    {w.score != null && <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
                      <span style={{ fontSize: 22, fontWeight: 800, color: col }}>{w.score.toFixed(1)}</span>
                      <span style={{ fontSize: 11, color: C.subtle }}>/10</span>
                    </div>}
                    {w.sleep && <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: C.muted }}>
                      <span style={{ fontWeight: 600, color: C.text }}>{w.sleep}h</span> sonno
                    </div>}
                    {w.kcal && <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: C.muted }}>
                      <span style={{ fontWeight: 600, color: C.text }}>{w.kcal}</span> kcal
                      {w.tdee && <span style={{ color: C.subtle }}>/ {Math.round(w.tdee)} target</span>}
                    </div>}
                  </div>
                </div>
              );
            })()}


            {/* Exercise previews */}
            {sess.exercises.map((ex, i) => {
              const prev = prevSessions.find(s => s.exercises.some(e => e.name.toLowerCase().trim() === ex.name.toLowerCase().trim()))?.exercises.find(e => e.name.toLowerCase().trim() === ex.name.toLowerCase().trim());
              return <ExercisePreview key={i} ex={ex} prevEx={prev} allSessions={db.sessions} onDelete={() => removeExercise(sess.id, i)} onSave={updatedEx => updateExercise(sess.id, i, updatedEx)} />;
            })}

            {/* ── Muscoli allenati ─────────────────────────────────────── */}
            {(()=>{
              const getISOWeekKey = d => {
                const dt = new Date(d);
                dt.setHours(0,0,0,0);
                dt.setDate(dt.getDate()+4-(dt.getDay()||7));
                const y = dt.getFullYear();
                const w = Math.ceil(((dt-new Date(y,0,1))/86400000+1)/7);
                return `${y}-W${String(w).padStart(2,'0')}`;
              };
              const sessWeekKey = getISOWeekKey(sess.date);
              const todayWeekKey = getISOWeekKey(new Date().toISOString().split('T')[0]);
              const weekSessions = db.sessions.filter(s => getISOWeekKey(s.date) === sessWeekKey);
              const muscleData = assessWeeklyVolume(weekSessions, findExercise, todayWeekKey);
              const { unmapped } = calcMuscleVolume(sess.exercises, findExercise);
              if (!muscleData.length) return null;
              const statusColor = st => st === 'over_mrv' ? C.red : st === 'over_mav' ? C.amber : st === 'in_mav' ? C.green : C.subtle;
              const statusLabel = st => st === 'over_mrv' ? 'sopra MRV' : st === 'over_mav' ? 'sopra MAV' : st === 'in_mav' ? 'in MAV' : 'sotto MEV';
              return (
                <div style={{marginTop:24}}>
                  <div style={{fontSize:13,fontWeight:600,color:C.subtle,letterSpacing:0.4,
                    textTransform:'uppercase',marginBottom:12,fontFamily:FONT_BODY}}>
                    Muscoli allenati (settimana)
                  </div>
                  {muscleData.isCurrentWeek && (
                    <div style={{fontSize:12,color:C.subtle,marginBottom:10,fontFamily:FONT_BODY}}>
                      Settimana in corso — dati parziali
                    </div>
                  )}
                  {unmapped.length > 0 && (
                    <div style={{background:'rgba(255,159,10,0.10)',borderRadius:10,
                      padding:'10px 14px',marginBottom:12,fontSize:13,color:'#B45309',fontFamily:FONT_BODY}}>
                      {unmapped.length} esercizi non riconosciuti — il volume muscolare potrebbe essere sottostimato.
                    </div>
                  )}
                  <div style={{display:'flex',flexDirection:'column',gap:10}}>
                    {muscleData.map(({muscle,effectiveSets,landmark,status,expertEstimate})=>{
                      const maxDisplay = Math.max(landmark.mrv, effectiveSets, 1);
                      const fillPct = Math.min(effectiveSets / maxDisplay * 100, 100);
                      const col = statusColor(status);
                      return (
                        <div key={muscle}>
                          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
                            marginBottom:4,fontFamily:FONT_BODY}}>
                            <span style={{fontSize:14,fontWeight:500,color:C.text,textTransform:'capitalize'}}>
                              {muscle}
                            </span>
                            <span style={{fontSize:13,color:col,fontWeight:600}}>
                              {effectiveSets} serie · {statusLabel(status)}
                            </span>
                          </div>
                          <div style={{height:6,borderRadius:3,background:'rgba(120,120,128,0.12)',overflow:'hidden'}}>
                            <div style={{height:'100%',borderRadius:3,background:col,
                              width:`${fillPct}%`,transition:'width 0.3s ease'}}/>
                          </div>
                          <div style={{display:'flex',justifyContent:'space-between',marginTop:3,fontFamily:FONT_BODY}}>
                            <span style={{fontSize:10,color:C.subtle}}>MEV {landmark.mev}</span>
                            <span style={{fontSize:10,color:C.subtle}}>MAV {landmark.mav_lo}-{landmark.mav_hi}</span>
                            <span style={{fontSize:10,color:C.subtle}}>MRV {landmark.mrv}</span>
                          </div>
                          {expertEstimate && (
                            <div style={{fontSize:10,color:C.subtle,marginTop:1,fontFamily:FONT_BODY}}>
                              (stima esperto, non peer-reviewed)
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

          </div>
        </div>

        {/* Confirm delete modal */}
        {confirmDeleteId && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 600, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', background: 'rgba(0,0,0,0.32)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
            onClick={() => setConfirmDeleteId(null)}>
            <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 560, background: '#fff', borderRadius: '16px 16px 0 0', padding: '28px 24px', paddingBottom: 'max(28px, calc(env(safe-area-inset-bottom, 0px) + 28px))' }}>
              <div style={{ fontFamily: FONT_BODY, fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 8 }}>Elimina sessione</div>
              <div style={{ fontFamily: FONT_BODY, fontSize: 15, color: C.subtle, marginBottom: 28, lineHeight: 1.5 }}>
                Vuoi eliminare <span style={{ color: C.text, fontWeight: 600 }}>{sess.workoutName || 'Allenamento'}</span>? L'operazione non è reversibile.
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setConfirmDeleteId(null)} style={{ flex: 1, padding: '14px 0', borderRadius: 8, border: 'none', background: 'rgba(120,120,128,0.12)', fontFamily: FONT_BODY, fontSize: 16, fontWeight: 600, color: C.text, cursor: 'pointer' }}>
                  Annulla
                </button>
                <button onClick={() => { del(confirmDeleteId); setConfirmDeleteId(null); setOpenId(null); }} style={{ flex: 1, padding: '14px 0', borderRadius: 8, border: 'none', background: C.red, fontFamily: FONT_BODY, fontSize: 16, fontWeight: 600, color: '#fff', cursor: 'pointer' }}>
                  Elimina
                </button>
              </div>
            </div>
          </div>
        )}
        </>
      );
    }
  }

  // ─── SESSION LIST ─────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`@keyframes gpSlideUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <SchedeWidget schede={(db.schede||[]).filter(s=>s.active!==false)} sessions={db.sessions} activeScheda={activeScheda} onSelect={setActiveScheda} colorMap={colorMap} />
      <div style={{ fontSize: 13, fontWeight: 600, color: C.subtle, letterSpacing: 0.4, textTransform: 'uppercase', fontFamily: FONT_BODY, paddingLeft: 4, marginBottom: 10 }}>
        Cronologia
      </div>
      <div style={{ marginBottom: 14 }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Cerca esercizio o scheda…" />
      </div>

      {visible.length === 0 && (
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <div style={{ fontSize: 14, color: C.subtle }}>{db.sessions.length === 0 ? "Nessun allenamento ancora. Registra la tua prima sessione per iniziare." : "Nessun risultato per questa ricerca."}</div>
        </div>
      )}

      {grouped.map(([weekKey, sessions], gi) => (
        <div key={weekKey} style={{ marginBottom: 24, animation: `gpSlideUp 0.45s ${gi * 0.06}s cubic-bezier(0.22,1,0.36,1) both` }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.subtle, textTransform: "uppercase", letterSpacing: 0.4, paddingLeft: 4, marginBottom: 8, fontFamily: FONT_BODY }}>{weekKey.split('__')[1]}</div>
          <div style={{ ...CARD_STYLE, overflow: "hidden" }}>
            {sessions.map((sess, si) => {
              const prevSessions = db.sessions.filter(s => s.date < sess.date);
              const vol = Math.round(sess.exercises.reduce((s, e) => s + calcVolume(e.sets), 0));
              const exTrends = sess.exercises.map(ex => {
                const prev = prevSessions.find(s => s.exercises.some(e => e.name.toLowerCase().trim() === ex.name.toLowerCase().trim()))?.exercises.find(e => e.name.toLowerCase().trim() === ex.name.toLowerCase().trim());
                const d = prev != null ? ((calcVolume(ex.sets) - calcVolume(prev.sets)) / (calcVolume(prev.sets) || 1) * 100) : null;
                if (d !== null && d > 1) return 'up';
                if (d !== null && d < -1) return 'down';
                return 'flat';
              });
              const nUp = exTrends.filter(t => t === 'up').length;
              const nDown = exTrends.filter(t => t === 'down').length;
              const stallNames = detectStalls([sess, ...prevSessions]);
              const nStall = sess.exercises.filter(ex => stallNames.includes(ex.name)).length;
              return (
                <div key={sess.id} id={`sess-${sess.date}`}>
                  <div style={{ padding: "16px 20px", cursor: "pointer", fontFamily: FONT_BODY, WebkitTapHighlightColor: "transparent", transition: "opacity 0.15s" }}
                    onClick={() => setOpenId(sess.id)}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 17, color: C.text, marginBottom: 3, letterSpacing: -0.3 }}>
                          {sess.workoutName || "Allenamento"}
                        </div>
                        <div style={{ fontSize: 13, color: C.subtle, fontWeight: 400 }}>
                          {new Date(sess.date).toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                          <span style={{ margin: "0 5px", opacity: 0.4 }}>·</span>
                          {sess.exercises.length} esercizi
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0, marginLeft: 14, justifyContent: "center" }}>
                        {vol > 0 && <span style={{ fontSize: 14, fontWeight: 600, color: C.text, letterSpacing: -0.3 }}>
                          {vol >= 1000 ? `${(vol / 1000).toFixed(1)}k` : vol}
                          <span style={{ fontWeight: 400, color: C.subtle, marginLeft: 3 }}>vol</span>
                        </span>}
                        {sess.wellness?.score != null && (() => {
                          const w = sess.wellness;
                          const col = w.score >= 7 ? C.green : w.score >= 5 ? C.blue : C.amber;
                          return <span style={{ fontSize: 10, fontWeight: 700, color: col, marginTop: 4 }}>{w.score.toFixed(1)}/10</span>;
                        })()}
                      </div>
                    </div>
                    {(nUp > 0 || nDown > 0 || nStall > 0) && (
                      <div style={{ display: "flex", gap: 5, marginTop: 9 }}>
                        {nUp > 0 && (<span style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "3px 9px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: C.greenL, color: C.green }}>
                          <NavArrowUp width={9} height={9}/>{nUp}
                        </span>)}
                        {nDown > 0 && (<span style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "3px 9px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: C.redL, color: C.red }}>
                          <NavArrowDown width={9} height={9}/>{nDown}
                        </span>)}
                        {nStall > 0 && (<span style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "3px 9px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: `${C.amber}18`, color: C.amber }}>
                          <WarningTriangle width={9} height={9}/>{nStall}
                        </span>)}
                      </div>
                    )}
                  </div>
                  {si < sessions.length - 1 && (
                    <div style={{ height: "0.5px", background: "rgba(60,60,67,0.12)", margin: "0 0 0 20px" }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {hiddenCount > 0 && !isFiltered && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "8px 0 24px", gap: 6 }}>
          <div style={{ fontSize: 12, color: C.subtle, fontFamily: FONT_BODY }}>Mostro solo il mese corrente</div>
          <button onClick={() => setShowAll(true)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontFamily: FONT_BODY, fontSize: 15, fontWeight: 500, color: C.blue, padding: 0,
            }}>
            Mostra tutto ({hiddenCount} sessioni precedenti)
          </button>
        </div>
      )}
    </>
  );
}

export function HistoryPage({ db, setDB, onBack }) {
  return (
    <div style={St.wrap}>
      <div style={{ paddingTop: "max(56px, calc(env(safe-area-inset-top, 0px) + 14px))", paddingRight: "20px", paddingBottom: "8px", paddingLeft: "20px", background: C.bg }}>
        {onBack && (
          <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: C.blue, padding: '0 0 10px', fontFamily: FONT_BODY, fontSize: 17, fontWeight: 400 }}>
            <NavArrowLeft width={16} height={16} strokeWidth={2} />
            Indietro
          </button>
        )}
        <h1 style={{ margin: 0, fontFamily: FONT_BODY, fontWeight: 800, fontSize: 34, color: C.text, letterSpacing: -1.2, lineHeight: 1.05 }}>Cronologia</h1>
        <p style={{ margin: "5px 0 0", fontSize: 14, color: C.subtle, fontWeight: 400, letterSpacing: 0 }}>{db.sessions.length} sessioni registrate</p>
      </div>
      <div style={{ padding: "8px 16px 0" }}>
        <HistorySection db={db} setDB={setDB} />
      </div>
    </div>
  );
}
